import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  ComputerDesktopIcon,
  TruckIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  PlusIcon,
  CubeIcon,
  EyeIcon,
  PhotoIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import {
  useGetAssetsQuery,
  useGetAssetRequestsQuery,
  useCreateAssetRequestMutation,
  useFulfillAssetRequestMutation
} from '../../services/assetApi';
import { Card, Button, Modal, Input, Select, Textarea } from '../../components/common';
import { setRequestFilters } from '../../store/slices/assetSlice';
import { toast } from 'react-hot-toast';
import socketService from '../../services/socketService';
import { assetApi } from '../../services/assetApi';

// AuthenticatedImage component for displaying images securely
const AuthenticatedImage = ({ fileId, authToken, className, onClick, ...props }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!fileId || !authToken) return;

    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/assets/images/${fileId}?token=${authToken}`);
        if (!response.ok) {
          throw new Error('Failed to load image');
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setImageUrl(url);
      } catch (err) {
        console.error('Error loading authenticated image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadImage();

    // Cleanup blob URL
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [fileId, authToken]);

  if (loading) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center text-gray-400`}>
        <PhotoIcon className="w-8 h-8" />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      className={className}
      onClick={onClick}
      {...props}
    />
  );
};

const EmployeeAssetDashboard = () => {
  const { t } = useTranslation(['common', 'assets']);
  const dispatch = useDispatch();
  const { user, token: authToken } = useSelector((state) => state.auth);

  // Modal states
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showMaintenanceRequestModal, setShowMaintenanceRequestModal] = useState(false);
  const [imagePreviewModal, setImagePreviewModal] = useState({ isOpen: false, image: null });
  const [assetDetailsModal, setAssetDetailsModal] = useState({ isOpen: false, asset: null });
  const [receiptForms, setReceiptForms] = useState({});



  // Form state
  const [requestForm, setRequestForm] = useState({
    requestType: 'new',
    assetType: '',
    purpose: '',
    priority: 'medium',
    remarks: ''
  });

  const [maintenanceForm, setMaintenanceForm] = useState({
    assetId: '',
    reason: '',
    priority: 'medium',
    description: '',
    remarks: ''
  });

  // API hooks
  const { data: assetsData, isLoading: assetsLoading, refetch: refetchAssets } = useGetAssetsQuery({
    status: 'assigned',
    assignedTo: user?.employeeId, // Filter assets by current logged-in employee
    page: 1,
    limit: 100 // Get all assigned assets for this user
  });

  const { data: requestsData, isLoading: requestsLoading, refetch: refetchRequests } = useGetAssetRequestsQuery({
    page: 1,
    limit: 10
  });

  // Connect to socket service on component mount (after hooks are declared)
  useEffect(() => {
    socketService.connect();

    // SocketService now handles all asset real-time updates automatically
    // RTK Query will automatically refetch when cache is invalidated by socketService

  }, []); // No dependencies needed - socketService handles everything



  const [createRequest, { isLoading: creating }] = useCreateAssetRequestMutation();
  const [fulfillRequest, { isLoading: fulfilling }] = useFulfillAssetRequestMutation();

  // Filter assets assigned to current user
  const myAssets = assetsData?.data || [];

  // Handle create request
  const handleCreateRequest = async (e) => {
    e.preventDefault();
    try {
      await createRequest(requestForm).unwrap();
      toast.success('Asset request submitted successfully');
      setShowRequestModal(false);
      setRequestForm({
        requestType: 'new',
        assetType: '',
        purpose: '',
        priority: 'medium',
        remarks: ''
      });
      refetchRequests();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to submit request');
    }
  };

  // Handle maintenance request
  const handleMaintenanceRequest = (asset) => {
    setMaintenanceForm({
      assetId: asset._id,
      reason: '',
      priority: 'medium',
      description: '',
      remarks: ''
    });
    setShowMaintenanceRequestModal(true);
  };

  const handleCreateMaintenanceRequest = async (e) => {
    e.preventDefault();
    try {
      // For now, we'll create a regular asset request with maintenance type
      await createRequest({
        requestType: 'repair',
        assetType: 'maintenance',
        purpose: `Maintenance Request: ${maintenanceForm.reason}\n\nDescription: ${maintenanceForm.description}\n\nAsset: ${maintenanceForm.assetId}`,
        priority: maintenanceForm.priority,
        remarks: maintenanceForm.remarks
      }).unwrap();
      toast.success('Maintenance request submitted successfully');
      setShowMaintenanceRequestModal(false);
      setMaintenanceForm({
        assetId: '',
        reason: '',
        priority: 'medium',
        description: '',
        remarks: ''
      });
      refetchRequests();
      refetchAssets();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to submit maintenance request');
    }
  };

  const assetTypeOptions = [
    { value: 'laptop', label: 'Laptop' },
    { value: 'desktop', label: 'Desktop' },
    { value: 'printer', label: 'Printer' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'tablet', label: 'Tablet' },
    { value: 'monitor', label: 'Monitor' },
    { value: 'keyboard', label: 'Keyboard' },
    { value: 'mouse', label: 'Mouse' },
    { value: 'headset', label: 'Headset' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <>
      <Helmet>
        <title>My Assets - HRMS</title>
      </Helmet>

      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent truncate flex items-center">
                  <ComputerDesktopIcon className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3 text-blue-600" />
                  My Assets
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
                  View and request company assets
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowRequestModal(true)}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Request Asset
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-blue-100 rounded-md">
                    <ComputerDesktopIcon className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">Assigned Assets</p>
                    <p className="text-lg font-bold text-gray-900">{myAssets.length}</p>
                  </div>
                </div>
              </div>
            </div>
            {/* <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-yellow-100 rounded-md">
                    <ClockIcon className="h-4 w-4 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">Under Maintenance</p>
                    <p className="text-lg font-bold text-gray-900">{myAssets.filter(asset => asset.currentStatus === 'under_maintenance').length}</p>
                  </div>
                </div>
              </div>
            </div> */}
            <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-yellow-100 rounded-md">
                    <ClockIcon className="h-4 w-4 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">Pending Requests</p>
                    <p className="text-lg font-bold text-gray-900">
                      {requestsData?.stats?.submittedRequests || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-green-100 rounded-md">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">Approved Requests</p>
                    <p className="text-lg font-bold text-gray-900">
                      {requestsData?.stats?.approvedRequests || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-purple-100 rounded-md">
                    <TruckIcon className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">Fulfilled Requests</p>
                    <p className="text-lg font-bold text-gray-900">
                      {requestsData?.stats?.fulfilledRequests || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* My Assets */}
          <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-4 mb-4 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ComputerDesktopIcon className="h-5 w-5 mr-2 text-blue-600" />
              My Assigned Assets
            </h3>
            {assetsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : myAssets.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myAssets.map(asset => {
                  const firstImage = asset.images?.[0]; // Get the first image

                  // Get the current assignment condition - properly handle data structure
                  let currentCondition = 'Unknown';
                  if (asset.currentCondition && typeof asset.currentCondition === 'string') {
                    currentCondition = asset.currentCondition;
                  } else if (asset.assignments) {
                    let assignmentsArray = [];
                    if (Array.isArray(asset.assignments)) {
                      assignmentsArray = asset.assignments;
                    } else if (typeof asset.assignments === 'object' && asset.assignments.assigned && Array.isArray(asset.assignments.assigned)) {
                      assignmentsArray = asset.assignments.assigned;
                    }

                    if (assignmentsArray.length > 0) {
                      // Sort assignments by assignedAt date (most recent first) - create a copy to avoid mutating Redux state
                      const sortedAssignments = [...assignmentsArray].sort((a, b) =>
                        new Date(b.assignedAt) - new Date(a.assignedAt)
                      );

                      // Find the most recent assignment that is still active (no returnedAt date)
                      // or get the latest assignment's assigned condition
                      let currentAssignment = sortedAssignments.find(assignment => !assignment.returnedAt);

                      // If no active assignment found, use the most recent one
                      if (!currentAssignment) {
                        currentAssignment = sortedAssignments[0];
                      }

                      // Extract condition from the current assignment
                      if (currentAssignment && currentAssignment.condition) {
                        if (typeof currentAssignment.condition === 'object') {
                          // Handle new structure: {assigned: "good", returned: "fair"}
                          currentCondition = currentAssignment.condition.assigned || 'Unknown';
                        } else if (typeof currentAssignment.condition === 'string') {
                          // Handle legacy string format
                          currentCondition = currentAssignment.condition;
                        }
                      }
                    }
                  }

                  const latestAssignment = asset.assignments?.[asset.assignments.length - 1];

                  return (
                    <div key={asset._id} className="group relative bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 border border-white/60 rounded-2xl shadow-lg hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-500 overflow-hidden backdrop-blur-sm">
                      {/* Background Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                      {/* Top Status Bar */}
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 via-blue-500 to-purple-600"></div>

                      {/* Asset Image Section */}
                      <div className="relative h-36 overflow-hidden">
                        {firstImage ? (
                          <div className="relative h-full">
                            <AuthenticatedImage
                              fileId={firstImage.fileId}
                              authToken={authToken}
                              alt={asset.assetName}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                              onClick={async () => {
                                try {
                                  const response = await fetch(`${process.env.REACT_APP_API_URL}/assets/images/${firstImage.fileId}?token=${authToken}`);
                                  if (response.ok) {
                                    const blob = await response.blob();
                                    const blobUrl = URL.createObjectURL(blob);
                                    setImagePreviewModal({ isOpen: true, image: blobUrl });
                                  } else {
                                    console.error('Failed to fetch image');
                                    toast.error('Failed to load image preview');
                                  }
                                } catch (error) {
                                  console.error('Error loading image preview:', error);
                                  toast.error('Failed to load image preview');
                                }
                              }}
                            />

                            {/* Image Type Indicator */}
                            <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full font-medium flex items-center space-x-1">
                              <PhotoIcon className="h-3 w-3" />
                              <span>{asset.images.length} image{asset.images.length !== 1 ? 's' : ''}</span>
                            </div>

                            {/* Click to View Overlay */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center cursor-pointer" onClick={async () => {
                              try {
                                const response = await fetch(`${process.env.REACT_APP_API_URL}/assets/images/${firstImage.fileId}?token=${authToken}`);
                                if (response.ok) {
                                  const blob = await response.blob();
                                  const blobUrl = URL.createObjectURL(blob);
                                  setImagePreviewModal({ isOpen: true, image: blobUrl });
                                }
                              } catch (error) {
                                toast.error('Failed to load image preview');
                              }
                            }}>
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white font-semibold text-sm bg-black/50 px-3 py-2 rounded-full">
                                👁️ Click to View
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="h-full bg-gradient-to-br from-slate-200 via-gray-200 to-slate-300 flex flex-col items-center justify-center">
                            <div className="relative">
                              <ComputerDesktopIcon className="h-16 w-16 text-gray-400 mb-3" />
                              <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-white">✨</span>
                              </div>
                            </div>
                            <span className="text-sm font-medium text-gray-600 mb-1">No Images Yet</span>
                            <span className="text-xs text-gray-500">Coming Soon</span>
                          </div>
                        )}

                        {/* Status Badge */}
                        <div className="absolute top-3 right-3">
                          <div className="bg-green-500/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center space-x-1">
                            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                            <span>ASSIGNED</span>
                          </div>
                        </div>
                      </div>

                      {/* Asset Information */}
                      <div className="relative p-5">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start space-x-3 min-w-0 flex-1">
                            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                              <ComputerDesktopIcon className="h-6 w-6 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="font-bold text-gray-900 text-lg leading-tight group-hover:text-blue-600 transition-colors duration-300">
                                {asset.assetName}
                              </h3>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                                  {asset.assetId}
                                </span>
                                <span className="px-2 py-1 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 text-xs font-semibold rounded-lg capitalize">
                                  {asset.category}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          {asset.model && (
                            <div className="bg-gray-50 rounded-lg p-3">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Model</div>
                              <div className="text-sm font-bold text-gray-800 truncate">{asset.model}</div>
                            </div>
                          )}

                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Value</div>
                            <div className="text-sm font-bold text-gray-800">₹{asset.purchaseCost?.toLocaleString() || 'N/A'}</div>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Assigned Date</div>
                            <div className="text-sm font-bold text-gray-800">
                              {latestAssignment && latestAssignment.assignedAt ? new Date(latestAssignment.assignedAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              }) : 'N/A'}
                            </div>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Condition</div>
                            <div className={`text-sm font-bold capitalize ${
                              currentCondition === 'excellent' ? 'text-green-600' :
                              currentCondition === 'good' ? 'text-blue-600' :
                              currentCondition === 'fair' ? 'text-yellow-600' :
                              currentCondition === 'poor' ? 'text-red-600' :
                              'text-gray-600'
                            }`}>
                              {currentCondition}
                            </div>
                          </div>
                        </div>

                        {/* Additional Info Row */}
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-4">
                          {asset.serialNumber && (
                            <div className="flex items-center space-x-1">
                              <span className="font-medium">S/N:</span>
                              <span className="bg-gray-100 px-2 py-1 rounded text-gray-700 font-mono">{asset.serialNumber}</span>
                            </div>
                          )}
                          {asset.vendor && (
                            <div className="flex items-center space-x-1">
                              <span className="font-medium">Vendor:</span>
                              <span className="text-gray-800 font-medium">{asset.vendor}</span>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            {[CubeIcon, EyeIcon, WrenchScrewdriverIcon].map((Icon, index) => {
                              const firstImage = asset.images?.[0];
                              return (
                                <button
                                  key={index}
                                  className={`group/btn relative w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 ${
                                    index === 0
                                      ? 'bg-gradient-to-br from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 shadow-purple-500/25 hover:shadow-purple-500/40'
                                      : index === 1
                                      ? 'bg-gradient-to-br from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 shadow-blue-500/25 hover:shadow-blue-500/40'
                                      : 'bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-amber-500/25 hover:shadow-amber-500/40'
                                  } shadow-lg hover:shadow-xl transform hover:scale-110`}
                                  title={
                                    index === 0 ? 'Asset Details' :
                                    index === 1 ? 'View Images' :
                                    'Request Maintenance'
                                  }
                                  onClick={() => {
                                    if (index === 1) { // Eye icon - view images
                                      if (firstImage) {
                                        const handleImageView = async () => {
                                          try {
                                            const response = await fetch(`${process.env.REACT_APP_API_URL}/assets/images/${firstImage.fileId}?token=${authToken}`);
                                            if (response.ok) {
                                              const blob = await response.blob();
                                              const blobUrl = URL.createObjectURL(blob);
                                              setImagePreviewModal({ isOpen: true, image: blobUrl });
                                            } else {
                                              toast('Failed to load asset image', { type: 'error' });
                                            }
                                          } catch (error) {
                                            console.error('Error loading image:', error);
                                            toast('Failed to load asset image', { type: 'error' });
                                          }
                                        };
                                        handleImageView();
                                      } else {
                                        toast('No images available for this asset', { type: 'info' });
                                      }
                                    } else if (index === 2) { // Wrench icon - request maintenance
                                      if (asset.currentStatus !== 'under_maintenance') {
                                        handleMaintenanceRequest(asset);
                                      } else {
                                        toast('This asset is already under maintenance', { type: 'info' });
                                      }
                                    } else {
                                      // Cube icon - show asset details modal
                                      setAssetDetailsModal({ isOpen: true, asset: asset });
                                    }
                                  }}
                                  disabled={index === 2 && asset.currentStatus === 'under_maintenance'}
                                >
                                  <Icon className="h-4 w-4 text-white drop-shadow-sm" />
                                  <div className={`absolute inset-0 rounded-xl opacity-0 group-hover/btn:opacity-20 transition-opacity duration-300 ${
                                    index === 0
                                      ? 'bg-gradient-to-br from-purple-400 to-pink-500'
                                      : index === 1
                                      ? 'bg-gradient-to-br from-blue-400 to-cyan-500'
                                      : 'bg-gradient-to-br from-amber-400 to-orange-500'
                                  }`}></div>
                                </button>
                              );
                            })}
                          </div>

                          {/* Status Indicator */}
                          <div className="flex items-center space-x-2">
                            {asset.currentStatus === 'under_maintenance' ? (
                              <>
                                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                                <span className="text-xs font-bold text-yellow-700 tracking-wide">MAINTENANCE</span>
                              </>
                            ) : (
                              <>
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                <span className="text-xs font-bold text-gray-700 tracking-wide">ACTIVE</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Bottom Accent */}
                      <div className="h-1 bg-gradient-to-r from-transparent via-purple-200 to-transparent"></div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <ComputerDesktopIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No assets currently assigned to you</p>
              </div>
            )}
          </div>

          {/* My Asset Requests */}
          <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-white/60 p-6 shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg">
                  <DocumentTextIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">My Asset Requests</h3>
                  <p className="text-sm text-gray-600">Track your asset requests progress</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {requestsData?.data?.length || 0} Total
                </span>
              </div>
            </div>

            {requestsLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : requestsData?.data?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {requestsData.data.slice(0, 6).map((request, index) => {
                  const statusSteps = [
                    { label: 'Submitted', status: 'submitted', color: 'yellow' },
                    { label: 'Approved', status: 'approved', color: 'green' },
                    { label: 'Fulfilled', status: 'fulfilled', color: 'purple' }
                  ];

                  const currentStepIndex = statusSteps.findIndex(step => step.status === request.status);
                  const isRejected = request.status === 'rejected';

                  return (
                    <div key={request._id} className="group relative bg-gradient-to-br from-white via-blue-50/20 to-indigo-50/20 border border-white/60 rounded-2xl shadow-lg hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-500 overflow-hidden backdrop-blur-sm">
                      {/* Background Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-blue-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                      {/* Top Accent Bar */}
                      <div className={`absolute top-0 left-0 right-0 h-1 ${
                        request.status === 'fulfilled' ? 'bg-gradient-to-r from-purple-400 via-pink-500 to-purple-600' :
                        request.status === 'approved' ? 'bg-gradient-to-r from-green-400 via-emerald-500 to-green-600' :
                        request.status === 'rejected' ? 'bg-gradient-to-r from-red-400 via-pink-500 to-red-600' :
                        'bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500'
                      }`}></div>

                      {/* Request Content */}
                      <div className="relative p-6">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start space-x-3 min-w-0 flex-1">
                            <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg transition-all duration-300 ${
                              request.status === 'fulfilled' ? 'bg-gradient-to-br from-purple-500 to-pink-600 shadow-purple-500/25' :
                              request.status === 'approved' ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-green-500/25' :
                              request.status === 'rejected' ? 'bg-gradient-to-br from-red-500 to-pink-600 shadow-red-500/25' :
                              'bg-gradient-to-br from-yellow-500 to-orange-600 shadow-yellow-500/25'
                            }`}>
                              {request.status === 'fulfilled' ? <TruckIcon className="h-6 w-6 text-white" /> :
                               request.status === 'approved' ? <CheckCircleIcon className="h-6 w-6 text-white" /> :
                               request.status === 'rejected' ? <ExclamationTriangleIcon className="h-6 w-6 text-white" /> :
                               <ClockIcon className="h-6 w-6 text-white" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <h4 className="font-bold text-gray-900 text-lg leading-tight group-hover:text-blue-600 transition-colors duration-300 capitalize">
                                {request.assetType} Request
                              </h4>
                              <div className="flex items-center space-x-2 mt-2">
                                <span className={`px-2 py-1 rounded-lg text-xs font-semibold capitalize ${
                                  request.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                                  request.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                  request.priority === 'medium' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {request.priority} Priority
                                </span>
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${
                                  request.status === 'fulfilled' ? 'bg-purple-100 text-purple-800' :
                                  request.status === 'approved' ? 'bg-green-100 text-green-800' :
                                  request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  <div className={`w-2 h-2 rounded-full mr-2 animate-pulse ${
                                    request.status === 'fulfilled' ? 'bg-purple-400' :
                                    request.status === 'approved' ? 'bg-green-400' :
                                    request.status === 'rejected' ? 'bg-red-400' :
                                    'bg-yellow-400'
                                  }`}></div>
                                  {request.status}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Purpose */}
                        <div className="mb-4">
                          <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg p-3 border border-gray-100">
                            "{request.purpose.length > 100 ? request.purpose.substring(0, 100) + '...' : request.purpose}"
                          </p>
                        </div>

                        {/* Progress Timeline */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                            <span className="font-medium">Request Progress</span>
                            <span>{currentStepIndex + 1} of {statusSteps.length} steps</span>
                          </div>

                          {/* Clean Progress Bar */}
                          <div className="bg-gray-100 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-500 ${
                                isRejected ? 'bg-red-400' :
                                request.status === 'submitted' ? 'bg-yellow-400' :
                                request.status === 'approved' ? 'bg-green-400' :
                                'bg-purple-400'
                              }`}
                              style={{
                                width: `${((currentStepIndex + 1) / statusSteps.length) * 100}%`
                              }}
                            ></div>
                          </div>

                          <div className="flex justify-between mt-2">
                            {statusSteps.map((step, stepIndex) => (
                              <div key={step.status} className="flex flex-col items-center">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                                  isRejected && stepIndex <= currentStepIndex ? 'bg-red-500 text-white' :
                                  stepIndex < currentStepIndex ? `bg-${step.color}-500 text-white` :
                                  stepIndex === currentStepIndex ? `bg-${step.color}-500 text-white shadow-lg animate-pulse` :
                                  'bg-gray-300 text-gray-600'
                                }`}>
                                  {isRejected && stepIndex <= currentStepIndex ? (
                                    <ExclamationTriangleIcon className="w-3 h-3" />
                                  ) : stepIndex < currentStepIndex ? (
                                    <CheckCircleIcon className="w-3 h-3" />
                                  ) : stepIndex === currentStepIndex ? (
                                    <ClockIcon className="w-3 h-3 animate-pulse" />
                                  ) : (
                                    <span>{stepIndex + 1}</span>
                                  )}
                                </div>
                                <span className={`text-xs font-medium mt-1 transition-colors duration-300 ${
                                  isRejected && stepIndex <= currentStepIndex ? 'text-red-600' :
                                  stepIndex <= currentStepIndex ? `text-${step.color}-600` :
                                  'text-gray-400'
                                }`}>
                                  {step.label}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Request Details */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Request Type</div>
                            <div className="text-sm font-bold text-gray-800 capitalize">{request.requestType}</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Requested On</div>
                            <div className="text-sm font-bold text-gray-800">
                              {new Date(request.requestedAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Status Message */}
                        <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
                          <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full animate-pulse ${
                              request.status === 'fulfilled' ? 'bg-purple-400' :
                              request.status === 'approved' ? 'bg-green-400' :
                              request.status === 'rejected' ? 'bg-red-400' :
                              'bg-yellow-400'
                            }`}></div>
                            <span className="text-xs font-medium text-gray-700">
                              {request.status === 'fulfilled' ? 'Asset has been delivered to you' :
                               request.status === 'approved' ? 'Your request has been approved' :
                               request.status === 'rejected' ? 'Request was not approved' :
                               'Request is being processed'}
                            </span>
                          </div>
                        </div>

                        {/* Asset Receipt Confirmation - Only for approved requests */}
                        {request.status === 'approved' && (
                          <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                            <div className="flex items-center space-x-2 mb-3">
                              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                              <span className="text-sm font-semibold text-green-800">Confirm Asset Receipt</span>
                            </div>

                            {!receiptForms[request._id]?.confirmed ? (
                              <form
                                onSubmit={async (e) => {
                                  e.preventDefault();
                                  const assetId = receiptForms[request._id]?.assetId || '';
                                  if (!assetId.trim()) {
                                    toast.error('Please enter the asset ID');
                                    return;
                                  }

                                  try {
                                    await fulfillRequest({
                                      id: request._id,
                                      assetId: assetId,
                                      condition: 'good' // Default condition for receipt confirmation
                                    }).unwrap();

                                    toast.success('Asset receipt confirmed successfully!');

                                    // Clear the form data - no longer needed as it's fulfilled
                                    setReceiptForms(prev => ({
                                      ...prev,
                                      [request._id]: { confirmed: true, assetId }
                                    }));

                                  } catch (error) {
                                    toast.error(error.data?.message || 'Failed to confirm asset receipt');
                                  }
                                }}
                                className="space-y-3"
                              >
                                <div>
                                  <Input
                                    type="text"
                                    placeholder="Enter Asset ID (e.g., LAP-001)"
                                    value={receiptForms[request._id]?.assetId || ''}
                                    onChange={(e) => setReceiptForms(prev => ({
                                      ...prev,
                                      [request._id]: { ...prev[request._id], assetId: e.target.value }
                                    }))}
                                    className="w-full bg-white border-green-200 focus:border-green-400 focus:ring-green-400"
                                    required
                                  />
                                  <p className="text-xs text-gray-600 mt-1">
                                    Enter the asset ID provided by your administrator
                                  </p>
                                </div>

                                <Button
                                  type="submit"
                                  loading={fulfilling}
                                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg transition-all duration-200"
                                >
                                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                                  Confirm Receipt
                                </Button>
                              </form>
                            ) : (
                              <div className="flex items-center space-x-2 text-sm">
                                <CheckCircleIcon className="h-4 w-4 text-green-600" />
                                <span className="text-green-800 font-medium">
                                  Asset receipt confirmed - Asset ID: <span className="font-bold">{receiptForms[request._id]?.assetId}</span>
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Bottom Accent */}
                      <div className={`h-1 bg-gradient-to-r from-transparent ${
                        request.status === 'fulfilled' ? 'via-purple-200 to-transparent' :
                        request.status === 'approved' ? 'via-green-200 to-transparent' :
                        request.status === 'rejected' ? 'via-red-200 to-transparent' :
                        'via-yellow-200 to-transparent'
                      }`}></div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30 rounded-2xl border-2 border-dashed border-gray-300">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <DocumentTextIcon className="h-8 w-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">No Asset Requests Yet</h4>
                <p className="text-gray-600 max-w-lg mx-auto mb-6">Start by requesting the assets you need for your work. All requests will appear here with real-time updates.</p>
                <Button
                  onClick={() => setShowRequestModal(true)}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Make First Request
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Request Asset Modal */}
      <Modal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        title={
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg">
              <PlusIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Request Asset
              </h3>
              <p className="text-sm text-gray-500">Submit a new asset request for approval</p>
            </div>
          </div>
        }
        size="lg"
      >
        <div className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30 -m-6 p-6 rounded-2xl border border-white/60 mb-6">
          <div className="text-center mb-6">
            <p className="text-sm text-gray-600">
              Fill out the form below to request a new asset. All requests require administrator approval.
            </p>
          </div>
        </div>

        <form onSubmit={handleCreateRequest} className="space-y-6">
          {/* Primary Selection Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
                <ComputerDesktopIcon className="h-4 w-4 mr-2 text-blue-600" />
                Request Type *
              </label>
              <Select
                required
                value={requestForm.requestType}
                onChange={(e) => setRequestForm({...requestForm, requestType: e.target.value})}
                className="bg-white/80 backdrop-blur-sm border-2 border-white/60 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="" disabled>Select request type</option>
                <option value="new">New Asset</option>
                <option value="replacement">Replacement</option>
                <option value="repair">Repair</option>
                <option value="upgrade">Upgrade</option>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
                <CubeIcon className="h-4 w-4 mr-2 text-indigo-600" />
                Asset Type *
              </label>
              <Select
                required
                value={requestForm.assetType}
                onChange={(e) => setRequestForm({...requestForm, assetType: e.target.value})}
                className="bg-white/80 backdrop-blur-sm border-2 border-white/60 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Choose asset type</option>
                {assetTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Priority Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
              <ExclamationTriangleIcon className="h-4 w-4 mr-2 text-orange-600" />
              Priority Level
            </label>
            <Select
              value={requestForm.priority}
              onChange={(e) => setRequestForm({...requestForm, priority: e.target.value})}
              className="bg-white/80 backdrop-blur-sm border-2 border-white/60 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 max-w-xs"
            >
              <option value="low">🟢 Low Priority</option>
              <option value="medium">🟡 Medium Priority</option>
              <option value="high">🟠 High Priority</option>
              <option value="urgent">🔴 Urgent Priority</option>
            </Select>
          </div>

          {/* Purpose Section */}
          <div className="space-y-3">
            <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2 text-green-600" />
              Purpose & Justification *
            </label>
            <div className="relative">
              <Textarea
                value={requestForm.purpose}
                onChange={(e) => setRequestForm({...requestForm, purpose: e.target.value})}
                placeholder="Please provide detailed justification for this asset request. Explain how it will benefit your work and productivity..."
                className="bg-white/80 backdrop-blur-sm border-2 border-white/60 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                required
                rows={4}
              />
              <div className="absolute bottom-3 right-3 text-xs text-gray-400 bg-white/80 px-2 py-1 rounded-lg">
                {requestForm.purpose.length}/500
              </div>
            </div>
            <p className="text-xs text-gray-500 ml-6">
              Be specific about your requirements and how this asset will support your role
            </p>
          </div>

          {/* Remarks Section */}
          <div className="space-y-3">
            <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
              <UserIcon className="h-4 w-4 mr-2 text-purple-600" />
              Additional Remarks
            </label>
            <div className="relative">
              <Textarea
                value={requestForm.remarks}
                onChange={(e) => setRequestForm({...requestForm, remarks: e.target.value})}
                placeholder="Any additional information or special requirements..."
                className="bg-white/80 backdrop-blur-sm border-2 border-white/60 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                rows={3}
              />
              <div className="absolute bottom-3 right-3 text-xs text-gray-400 bg-white/80 px-2 py-1 rounded-lg">
                {requestForm.remarks.length}/300
              </div>
            </div>
            <p className="text-xs text-gray-500 ml-6">
              Optional: Share any preferences or additional context
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-4 space-y-3 sm:space-y-0 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowRequestModal(false)}
              className="w-full sm:w-auto bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 py-3 px-6 font-semibold"
            >
              Cancel Request
            </Button>
            <Button
              type="submit"
              loading={creating}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 py-3 px-6 font-semibold transform hover:scale-105"
            >
              {creating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Info Footer */}
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
          <div className="flex items-start space-x-3">
            <div className="p-1 bg-blue-100 rounded-lg">
              <CheckCircleIcon className="h-4 w-4 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-blue-800 font-medium">What happens next?</p>
              <p className="text-sm text-blue-700 mt-1">
                Your request will be reviewed by an administrator within 1-2 business days.
                You'll receive a notification once your request is approved or needs clarification.
              </p>
            </div>
          </div>
        </div>
      </Modal>

      {/* Maintenance Request Modal */}
      <Modal
        isOpen={showMaintenanceRequestModal}
        onClose={() => setShowMaintenanceRequestModal(false)}
        title={
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl shadow-lg">
              <WrenchScrewdriverIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Request Maintenance
              </h3>
              <p className="text-sm text-gray-500">Report an issue with your assigned asset</p>
            </div>
          </div>
        }
        size="lg"
      >
        <div className="bg-gradient-to-br from-amber-50 via-orange-50/20 to-yellow-50/30 -m-6 p-6 rounded-2xl border border-amber-200 mb-6">
          <div className="text-center mb-6">
            <p className="text-sm text-amber-800">
              Fill out the form below to request maintenance for your assigned asset. This helps us prioritize and fix issues quickly.
            </p>
          </div>
        </div>

        <form onSubmit={handleCreateMaintenanceRequest} className="space-y-6">
          {/* Maintenance Reason */}
          <div className="space-y-3">
            <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
              <ExclamationTriangleIcon className="h-4 w-4 mr-2 text-red-600" />
              Issue Description *
            </label>
            <div className="relative">
              <Textarea
                value={maintenanceForm.description}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})}
                placeholder="Please describe the issue with your asset in detail. What symptoms are you experiencing? When did it start?..."
                className="bg-white/80 backdrop-blur-sm border-2 border-white/60 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                required
                rows={4}
              />
              <div className="absolute bottom-3 right-3 text-xs text-gray-400 bg-white/80 px-2 py-1 rounded-lg">
                {maintenanceForm.description.length}/300
              </div>
            </div>
            <p className="text-xs text-gray-500 ml-6">
              Be as specific as possible to help technicians understand and fix the issue
            </p>
          </div>

          {/* Priority Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
              <ClockIcon className="h-4 w-4 mr-2 text-orange-600" />
              Urgency Level *
            </label>
            <Select
              required
              value={maintenanceForm.priority}
              onChange={(e) => setMaintenanceForm({...maintenanceForm, priority: e.target.value})}
              className="bg-white/80 backdrop-blur-sm border-2 border-white/60 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 max-w-xs"
            >
              <option value="low">🟢 Low - Minor inconvenience</option>
              <option value="medium">🟡 Medium - Affects workflow</option>
              <option value="high">🟠 High - Urgent fix needed</option>
              <option value="urgent">🔴 Critical - Asset unusable</option>
            </Select>
          </div>

          {/* Reported By */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg">
                <UserIcon className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-blue-800">Reported By</p>
                <p className="text-sm text-blue-700">
                  {user?.firstName} {user?.lastName} • Employee ID: {user?.employeeId}
                </p>
              </div>
            </div>
          </div>

          {/* Additional Remarks */}
          <div className="space-y-3">
            <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2 text-purple-600" />
              Additional Notes
            </label>
            <div className="relative">
              <Textarea
                value={maintenanceForm.remarks}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, remarks: e.target.value})}
                placeholder="Any additional information that might help with the repair..."
                className="bg-white/80 backdrop-blur-sm border-2 border-white/60 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                rows={3}
              />
              <div className="absolute bottom-3 right-3 text-xs text-gray-400 bg-white/80 px-2 py-1 rounded-lg">
                {maintenanceForm.remarks.length}/200
              </div>
            </div>
            <p className="text-xs text-gray-500 ml-6">
              Optional: Special instructions, preferred repair times, etc.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-4 space-y-3 sm:space-y-0 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowMaintenanceRequestModal(false)}
              className="w-full sm:w-auto bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 py-3 px-6 font-semibold"
            >
              Cancel Request
            </Button>
            <Button
              type="submit"
              loading={creating}
              className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 py-3 px-6 font-semibold transform hover:scale-105"
            >
              {creating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <WrenchScrewdriverIcon className="h-4 w-4 mr-2" />
                  Request Maintenance
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Info Footer */}
        <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
          <div className="flex items-start space-x-3">
            <div className="p-1 bg-amber-100 rounded-lg">
              <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-amber-800 font-medium">Maintenance Process</p>
              <p className="text-sm text-amber-700 mt-1">
                Your maintenance request will be reviewed by administrators. You'll receive updates on schedule,
                technician assignment, and completion status via notifications.
              </p>
            </div>
          </div>
        </div>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        isOpen={imagePreviewModal.isOpen}
        onClose={() => setImagePreviewModal({ isOpen: false, image: null })}
        title="Asset Image"
        size="lg"
      >
        <div className="flex items-center justify-center min-h-[50vh]">
          {imagePreviewModal.image && (
            <img
              src={imagePreviewModal.image}
              alt="Asset Preview"
              className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
            />
          )}
        </div>
      </Modal>

      {/* Asset Details Modal */}
      <Modal
        isOpen={assetDetailsModal.isOpen}
        onClose={() => setAssetDetailsModal({ isOpen: false, asset: null })}
        title="Asset Details"
        size="lg"
      >
        {assetDetailsModal.asset && (() => {
          const asset = assetDetailsModal.asset;

          console.log('Asset data:', asset); // Debug log

          // Handle different data structures for assignments
          let assignmentsArray = [];
          if (asset.assignments) {
            if (Array.isArray(asset.assignments)) {
              assignmentsArray = asset.assignments;
            } else if (typeof asset.assignments === 'object' && asset.assignments.assigned && Array.isArray(asset.assignments.assigned)) {
              assignmentsArray = asset.assignments.assigned;
            }
          }

          console.log('Assignments array:', assignmentsArray); // Debug log

          const latestAssignment = assignmentsArray.length > 0 ? assignmentsArray[assignmentsArray.length - 1] : null;

          return (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <ComputerDesktopIcon className="h-12 w-12 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{asset.assetName}</h2>
                    <p className="text-sm text-gray-600">Asset ID: {asset.assetId}</p>
                    <div className="flex items-center mt-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${asset.category === 'laptop' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                        {asset.category || 'Other'}
                      </span>
                      <span className="ml-2 inline-flex items-center">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                        <span className="capitalize text-sm">assigned</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Basic Information</h3>
                  <dl className="space-y-3">
                    <div className="flex justify-between">
                      <dt className="text-sm font-medium text-gray-500">Asset Name</dt>
                      <dd className="text-sm text-gray-900">{asset.assetName}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm font-medium text-gray-500">Asset ID</dt>
                      <dd className="text-sm text-gray-900">{asset.assetId}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm font-medium text-gray-500">Category</dt>
                      <dd className="text-sm text-gray-900 capitalize">{asset.category || 'Other'}</dd>
                    </div>
                    {asset.model && (
                      <div className="flex justify-between">
                        <dt className="text-sm font-medium text-gray-500">Model</dt>
                        <dd className="text-sm text-gray-900">{asset.model}</dd>
                      </div>
                    )}
                    {asset.serialNumber && (
                      <div className="flex justify-between">
                        <dt className="text-sm font-medium text-gray-500">Serial Number</dt>
                        <dd className="text-sm text-gray-900">{asset.serialNumber}</dd>
                      </div>
                    )}
                    {asset.vendor && (
                      <div className="flex justify-between">
                        <dt className="text-sm font-medium text-gray-500">Vendor</dt>
                        <dd className="text-sm text-gray-900">{asset.vendor}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                {/* Assignment Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Assignment Information</h3>
                  <dl className="space-y-3">
                    <div className="flex justify-between">
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <dd className="text-sm text-gray-900 capitalize">assigned</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm font-medium text-gray-500">Assigned To</dt>
                      <dd className="text-sm text-gray-900">
                        {asset.currentHolder?.firstName} {asset.currentHolder?.lastName}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm font-medium text-gray-500">Assigned Date</dt>
                      <dd className="text-sm text-gray-900">
                        {latestAssignment?.assignedAt ? new Date(latestAssignment.assignedAt).toLocaleDateString() : 'N/A'}
                      </dd>
                    </div>
                    {asset.purchaseCost && (
                      <div className="flex justify-between">
                        <dt className="text-sm font-medium text-gray-500">Purchase Cost</dt>
                        <dd className="text-sm text-gray-900">₹{asset.purchaseCost.toLocaleString()}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>

              {/* Asset Images Gallery */}
              {asset.images && asset.images.length > 0 && (
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                      <PhotoIcon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Asset Images</h3>
                      <p className="text-sm text-gray-600">{asset.images.length} image{asset.images.length !== 1 ? 's' : ''} available</p>
                    </div>
                  </div>

                  {/* Images Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {asset.images.map((image, index) => (
                      <div key={image._id} className="group relative">
                        <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 cursor-pointer transform hover:scale-[1.02]">
                          {image.fileId ? (
                            <AuthenticatedImage
                              fileId={image.fileId}
                              authToken={authToken}
                              alt={`Asset image ${index + 1}`}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110 cursor-pointer"
                              onClick={async () => {
                                try {
                                  const response = await fetch(`${process.env.REACT_APP_API_URL}/assets/images/${image.fileId}?token=${authToken}`);
                                  if (response.ok) {
                                    const blob = await response.blob();
                                    const blobUrl = URL.createObjectURL(blob);
                                    setImagePreviewModal({ isOpen: true, image: blobUrl });
                                  } else {
                                    console.error('Failed to fetch image:', response.status, response.statusText);
                                    toast.error('Failed to load image preview');
                                  }
                                } catch (error) {
                                  console.error('Error loading image preview:', error);
                                  toast.error('Failed to load image preview');
                                }
                              }}
                            />
                          ) : (
                            // Fallback for legacy images (before GridFS)
                            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300 text-gray-400">
                              <svg className="w-8 h-8 mb-2 opacity-60" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                              </svg>
                              <span className="text-xs font-medium">Legacy Image</span>
                            </div>
                          )}

                          {/* Image Hover Overlay - Clickable */}
                          <div
                            className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 rounded-2xl flex items-center justify-center cursor-pointer"
                            onClick={async () => {
                              try {
                                const response = await fetch(`${process.env.REACT_APP_API_URL}/assets/images/${image.fileId}?token=${authToken}`);
                                if (response.ok) {
                                  const blob = await response.blob();
                                  const blobUrl = URL.createObjectURL(blob);
                                  setImagePreviewModal({ isOpen: true, image: blobUrl });
                                } else {
                                  console.error('Failed to fetch image:', response.status, response.statusText);
                                  toast.error('Failed to load image preview');
                                }
                              } catch (error) {
                                console.error('Error loading image preview:', error);
                                toast.error('Failed to load image preview');
                              }
                            }}
                          >
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-2 text-white font-semibold">
                              <EyeIcon className="h-6 w-6 mx-auto mb-1" />
                              <span className="text-xs">Click to View</span>
                            </div>
                          </div>

                          {/* Image Number Badge */}
                          <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full font-medium">
                            {index + 1}
                          </div>
                        </div>

                        {/* Image Info */}
                        <div className="mt-3 p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                              <span className="text-xs font-medium text-gray-700">Image {index + 1}</span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {(image.size ? (image.size / 1024).toFixed(1) : '0')} KB
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Click image to enlarge
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* No Images Placeholder */}
                  {(!asset.images || asset.images.length === 0) && (
                    <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl border-2 border-dashed border-gray-300">
                      <div className="w-16 h-16 bg-gradient-to-r from-gray-300 to-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                        <PhotoIcon className="h-8 w-8 text-gray-600" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">No Images Available</h4>
                      <p className="text-gray-600 max-w-sm mx-auto">This asset doesn't have any images uploaded yet.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <Button variant="secondary" onClick={() => setAssetDetailsModal({ isOpen: false, asset: null })}>
                  Close
                </Button>
                {asset.images && asset.images.length > 0 && (
                  <Button
                    onClick={async () => {
                      if (asset.images[0] && asset.images[0].fileId) {
                        try {
                          const response = await fetch(`${process.env.REACT_APP_API_URL}/assets/images/${asset.images[0].fileId}?token=${authToken}`);
                          if (response.ok) {
                            const blob = await response.blob();
                            const blobUrl = URL.createObjectURL(blob);
                            setImagePreviewModal({ isOpen: true, image: blobUrl });
                            setAssetDetailsModal({ isOpen: false, asset: null });
                          } else {
                            console.error('Failed to fetch image:', response.status, response.statusText);
                            toast.error('Failed to load image preview');
                          }
                        } catch (error) {
                          console.error('Error loading image preview:', error);
                          toast.error('Failed to load image preview');
                        }
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    View Images
                  </Button>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>
    </>
  );
};

export default EmployeeAssetDashboard;
